import React, { useEffect, useRef, useState } from 'react'
import { InputText } from 'primereact/inputtext'

const COMPLETE_HHMM = /^-?\d+:[0-5]\d$/

/**
 * Formata horas decimais (ex.: 1.5) como HH:mm, alinhado a formatTempoSlaHoras / addHoursToDate.
 * @param {number|string|null|undefined} valor
 * @returns {string}
 */
export function decimalHorasParaHHMM(valor) {
  if (valor == null || valor === '') {
    return ''
  }
  const numero = Number(valor)
  if (Number.isNaN(numero)) {
    return ''
  }
  const sinal = numero < 0 ? '-' : ''
  const totalMinutos = Math.round(Math.abs(numero) * 60)
  const horas = String(Math.floor(totalMinutos / 60)).padStart(2, '0')
  const minutos = String(totalMinutos % 60).padStart(2, '0')
  return `${sinal}${horas}:${minutos}`
}

/**
 * Interpreta HH:mm (minutos 0–59) como horas decimais.
 * Aceita também só dígitos: os dois últimos são minutos, o restante são horas (ex.: 0300 → 03:00, 0045 → 00:45, 0145 → 01:45).
 * @param {string|null|undefined} str
 * @returns {number|null}
 */
export function hhmmParaDecimalHoras(str) {
  if (str == null) {
    return null
  }
  const trimmed = String(str).trim()
  if (!trimmed) {
    return null
  }
  let sign = 1
  let body = trimmed
  if (body.startsWith('-')) {
    sign = -1
    body = body.slice(1).trim()
  }
  if (!body) {
    return null
  }
  const parts = body.split(':')
  if (parts.length === 2) {
    const h = parseInt(parts[0], 10)
    const m = parseInt(parts[1], 10)
    if (!Number.isFinite(h) || h < 0 || !Number.isFinite(m) || m < 0 || m > 59) {
      return null
    }
    const totalMinutos = h * 60 + m
    return sign * (totalMinutos / 60)
  }
  if (!/^\d+$/.test(body)) {
    return null
  }
  let h
  let m
  if (body.length === 1) {
    h = 0
    m = parseInt(body, 10)
  } else {
    m = parseInt(body.slice(-2), 10)
    h = parseInt(body.slice(0, -2) || '0', 10)
  }
  if (!Number.isFinite(m) || m < 0 || m > 59 || !Number.isFinite(h) || h < 0) {
    return null
  }
  return sign * ((h * 60 + m) / 60)
}

export default function LtsInputHoras(props) {
  const {
    onChange: onChangeProp,
    label,
    hidden,
    value = null,
    name,
    maxLength,
    autoFocus,
    style,
    disabled,
    className,
    placeholder = '00:00',
    id,
    inputId,
    onBlur: onBlurProp,
    ...rest
  } = props

  const fallbackIdRef = useRef(null)
  if (!fallbackIdRef.current) {
    fallbackIdRef.current = `lts-input-horas-${Math.random().toString(36).slice(2, 10)}`
  }
  const resolvedId = inputId ?? id ?? fallbackIdRef.current

  const [displayText, setDisplayText] = useState(() => decimalHorasParaHHMM(value))

  useEffect(() => {
    setDisplayText(decimalHorasParaHHMM(value))
  }, [value])

  const commit = (text) => {
    const parsed = hhmmParaDecimalHoras(text)
    if (parsed === null) {
      onChangeProp?.(null)
      setDisplayText('')
      return
    }
    onChangeProp?.(parsed)
    setDisplayText(decimalHorasParaHHMM(parsed))
  }

  const handleChange = (event) => {
    const text = event.target.value
    setDisplayText(text)
    if (COMPLETE_HHMM.test(text)) {
      const parsed = hhmmParaDecimalHoras(text)
      if (parsed !== null) {
        onChangeProp?.(parsed)
      }
    }
  }

  const handleBlur = (event) => {
    commit(event.target.value)
    onBlurProp?.(event)
  }

  return (
    <span className="p-float-label">
      <InputText
        {...rest}
        id={resolvedId}
        value={displayText}
        onChange={handleChange}
        onBlur={handleBlur}
        name={name}
        maxLength={maxLength}
        autoFocus={autoFocus}
        style={style}
        disabled={disabled}
        className={className}
        placeholder={placeholder}
        autoComplete="off"
      />
      {label && !hidden ? <label htmlFor={resolvedId}>{label}</label> : null}
    </span>
  )
}
